import { useCallback, useEffect, useRef, useState } from 'react'

import * as presentationService from '../services/presentationService'
import type { DeckDefinition, ResolvedBlock } from '../types/presentation.types'
import type { BlockView } from './useResolvedBlocks'

/**
 * Block content for the presenter, one story at a time (MYD-496).
 *
 * The presenter shows the SAVED deck, so it resolves through
 * `POST /presentations/:id/resolve` with `slideIds`, one story per request,
 * rather than the editor's per-slide preview. A story is "ready" when its
 * blocks have come back AND the images they show have loaded (or a short
 * timeout has passed), so the first flip onto a photo slide does not reveal a
 * half-drawn image.
 *
 * The caller asks for the story on screen and the one after it; the next is
 * therefore always resolving while the current one is being talked through.
 *
 * A failure never interrupts the show: the story's blocks become quiet error
 * placeholders, and the story still counts as ready.
 *
 * A published snapshot (MYD-508) passes its frozen blocks as `fixed`: nothing
 * is fetched, but a story still becomes ready only once its images, including
 * the captured maps and charts, have loaded.
 */

/** Longest the presenter waits on images before starting anyway. */
export const IMAGE_WAIT_MS = 4000

const failed = (): ResolvedBlock => ({ status: 'error', payload: null, message: 'Could not be loaded.' })

/** Every image URL a batch of payloads will show. */
export const imageUrlsIn = (blocks: Record<string, ResolvedBlock>): string[] =>
	Object.values(blocks).flatMap(block => {
		if (block.status !== 'ok') return []
		const urls = [(block.payload as { url?: unknown } | null)?.url, block.rasterUrl]
		return urls.filter((url): url is string => typeof url === 'string' && /^https:\/\//.test(url))
	})

const preload = (urls: string[]): Promise<void> => {
	if (urls.length === 0 || typeof Image === 'undefined') return Promise.resolve()
	const loads = urls.map(
		url =>
			new Promise<void>(resolve => {
				const image = new Image()
				image.onload = () => resolve()
				image.onerror = () => resolve()
				image.src = url
			}),
	)
	return Promise.race([Promise.all(loads).then(() => undefined), new Promise<void>(resolve => setTimeout(resolve, IMAGE_WAIT_MS))])
}

export interface UsePresentationResolver {
	blocks: Record<string, BlockView>
	isStoryReady: (storyIndex: number) => boolean
}

export function usePresentationResolver(
	presentationId: string | null,
	deck: DeckDefinition | null,
	wantedStories: number[],
	fixed?: Record<string, ResolvedBlock> | null,
): UsePresentationResolver {
	const [blocks, setBlocks] = useState<Record<string, BlockView>>({})
	const [ready, setReady] = useState<Set<number>>(() => new Set())
	const started = useRef(new Set<number>())

	const resolveStory = useCallback(
		async (storyIndex: number) => {
			const story = deck?.stories[storyIndex]
			if ((!presentationId && !fixed) || !story || started.current.has(storyIndex)) return
			started.current.add(storyIndex)

			const slideIds = story.slides.map(slide => slide.id)
			const blockIds = story.slides.flatMap(slide => slide.blocks.map(block => block.id))

			let resolved: Record<string, ResolvedBlock>
			try {
				if (fixed) resolved = Object.fromEntries(blockIds.filter(id => fixed[id]).map(id => [id, fixed[id]]))
				else resolved = slideIds.length && presentationId ? (await presentationService.resolvePresentation(presentationId, slideIds)).blocks : {}
			} catch {
				resolved = Object.fromEntries(blockIds.map(id => [id, failed()]))
			}
			// Anything the server left out would otherwise spin forever.
			for (const id of blockIds) if (!resolved[id]) resolved[id] = failed()

			setBlocks(previous => ({ ...previous, ...resolved }))
			await preload(imageUrlsIn(resolved))
			setReady(previous => new Set(previous).add(storyIndex))
		},
		[presentationId, deck, fixed],
	)

	const wantedKey = wantedStories.join(',')
	useEffect(() => {
		for (const storyIndex of wantedStories) resolveStory(storyIndex)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wantedKey, resolveStory])

	const isStoryReady = useCallback((storyIndex: number) => ready.has(storyIndex), [ready])

	return { blocks, isStoryReady }
}

export default usePresentationResolver
